import { ref, uploadBytes, getDownloadURL, deleteObject, listAll } from 'firebase/storage';
import { storage } from './config';

// content/poster フォルダ内の画像一覧（ダウンロードURL）を取得
export const listPosterImages = async () => {
  try {
    const posterRef = ref(storage, 'content/poster/prd');
    const res = await listAll(posterRef);
    const urlPromises = res.items.map(async (itemRef) => {
      const url = await getDownloadURL(itemRef);
      return { name: itemRef.name, url };
    });
    return await Promise.all(urlPromises);
  } catch (error) {
    console.error('Error listing poster images:', error);
    return [];
  }
};

// ユーザーアイコンをアップロード
export const uploadUserIcon = async (userId, file) => {
  try {
    // ファイルサイズチェック（5MB以下）
    if (file.size > 5 * 1024 * 1024) {
      throw new Error('ファイルサイズが大きすぎます（5MB以下にしてください）');
    }

    // ファイル形式チェック
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('対応していないファイル形式です（JPEG、PNG、GIFのみ）');
    }

    // ファイル拡張子を取得
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const newFileName = `icon.${fileExtension}`;
    
    // 既存の異なる拡張子のアイコンファイルを削除
    await deleteExistingUserIcons(userId, newFileName);
    
    // Storage参照を作成（user/icon/{userId}/icon.{extension}）
    const storageRef = ref(storage, `user/icon/${userId}/${newFileName}`);
    
    // ファイルをアップロード
    const snapshot = await uploadBytes(storageRef, file);
    
    // ダウンロードURLを取得
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return {
      url: downloadURL,
      path: snapshot.ref.fullPath,
      size: file.size,
      type: file.type
    };
  } catch (error) {
    console.error('Error uploading icon:', error);
    throw error;
  }
};

// ユーザーアイコンを削除
export const deleteUserIcon = async (userId, iconPath) => {
  try {
    const storageRef = ref(storage, iconPath);
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Error deleting icon:', error);
    throw error;
  }
};

// ユーザーアイコンフォルダ内の既存ファイルを削除（新しいファイル名以外）
export const deleteExistingUserIcons = async (userId, newFileName) => {
  try {
    const iconFolderRef = ref(storage, `user/icon/${userId}/`);
    const existingFiles = await listAll(iconFolderRef);
    
    // 新しいファイル名と異なる既存ファイルを削除（拡張子が違うicon.*ファイルも含む）
    const deletePromises = existingFiles.items
      .filter(item => {
        // icon.* 形式のファイルで、新しいファイル名と異なるものを削除対象とする
        const isIconFile = item.name.startsWith('icon.');
        return isIconFile && item.name !== newFileName;
      })
      .map(item => deleteObject(item));
    
    if (deletePromises.length > 0) {
      await Promise.all(deletePromises);
      console.log(`Deleted ${deletePromises.length} old icon file(s) for user ${userId}`);
    }
  } catch (error) {
    console.error('Error deleting existing icons:', error);
    // エラーがあっても新しいアップロードは続行するため、エラーを再スローしない
  }
};

// デフォルトアイコンのURLを生成
export const getDefaultIconUrl = () => {
  return '/img/craken2.png'; // デフォルトのクラーケンアイコン
};

// 画像ファイルをリサイズ（オプション機能）
export const resizeImage = (file, maxWidth = 200, maxHeight = 200, quality = 0.8) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // アスペクト比を維持してリサイズ
      let { width, height } = img;
      
      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // 画像を描画
      ctx.drawImage(img, 0, 0, width, height);
      
      // Blobとして出力
      canvas.toBlob(resolve, 'image/jpeg', quality);
    };
    
    img.src = URL.createObjectURL(file);
  });
};
